import { useState, useEffect, useRef } from "react";
import "./chartGuide.css";

function computeBelowPlacement({ rect, bubbleWidth, bubbleHeight, bottomNavbarHeight }) {
  const margin = 12;
  const gap = 24;
  const maxTop = window.innerHeight - bottomNavbarHeight - bubbleHeight - margin;
  const maxLeft = window.innerWidth - bubbleWidth - margin;

  const top = Math.min(Math.max(rect.bottom + gap, margin), Math.max(maxTop, margin));

  const centeredLeft = rect.left + rect.width / 2 - bubbleWidth / 2;
  const left = Math.min(Math.max(centeredLeft, margin), Math.max(maxLeft, margin));

  const ARROW_SIZE = 20;
  const targetCenter = rect.left + rect.width / 2;
  const rawPercent = ((targetCenter - left) / bubbleWidth) * 100;
  const limit = (ARROW_SIZE / bubbleWidth) * 100 + 4;
  const arrowOffset = `${Math.min(Math.max(rawPercent, limit), 100 - limit)}%`;

  return { top, left, arrow: "top", arrowOffset };
}

const PortfolioGuideSteps = ({ setShowGuide, onTabChange }) => {
  const steps = [
    {
      id: 1,
      target: "portfolio-guide-summary",
      text: "Free Cash shows what you have available to trade. Portfolio shows your total account value, updated as your positions and cash change.",
      tabToShow: null,
    },
    {
      id: 2,
      target: "portfolio-guide-open-orders",
      text: "Orders waiting to fill live here, including limit orders and stop losses. When an order fills, it moves into Holdings.",
      tabToShow: "openTrades",
    },
    {
      id: 3,
      target: "portfolio-guide-current-holdings",
      text: "Your open positions live here. Track how they’re performing, then tap any position for full details or to add a stop loss.",
      tabToShow: "holdings",
    },
    {
      id: 4,
      target: "portfolio-guide-closed-trades",
      text: "Every completed trade lives here. Multi order positions are grouped so you can expand them to see the full transaction history and realized P&L.",
      tabToShow: "closedTrades",
    },
  ];

  const [stepIndex, setStepIndex] = useState(0);
  const [bubblePosition, setBubblePosition] = useState({ top: -1000, left: -1000 });
  const [arrowPosition, setArrowPosition] = useState("bottom");
  const [arrowOffset, setArrowOffset] = useState("50%");
  const [isPositioned, setIsPositioned] = useState(false);
  const current = steps[stepIndex];
  const bubbleRef = useRef(null);

  const skipToNextStepRef = useRef(null);
  skipToNextStepRef.current = () => {
    if (stepIndex < steps.length - 1) {
      const nextStep = steps[stepIndex + 1];
      if (nextStep.tabToShow && onTabChange) {
        onTabChange(nextStep.tabToShow);
      }
      setIsPositioned(false);
      setBubblePosition({ top: -1000, left: -1000 });
      setStepIndex(stepIndex + 1);
      return;
    }
    setShowGuide(false);
  };

  useEffect(() => {
    const prevHighlighted = document.querySelectorAll(".chart-guide-highlight");
    prevHighlighted.forEach((el) => el.classList.remove("chart-guide-highlight"));

    const targetElement = document.getElementById(current.target);
    if (targetElement) {
      targetElement.classList.add("chart-guide-highlight");
    }

    return () => {
      const highlightedElement = document.getElementById(current.target);
      if (highlightedElement) {
        highlightedElement.classList.remove("chart-guide-highlight");
      }
    };
  }, [current.target]);

  useEffect(() => {
    const isMobile = window.innerWidth <= 768;
    const bottomNavbarHeight = isMobile ? 64 : 0;

    const compute = () => {
      const targetElement = document.getElementById(current.target);
      if (!targetElement) return;

      const rect = targetElement.getBoundingClientRect();
      const bubbleElement = bubbleRef.current;
      const fallbackWidth = isMobile ? 280 : 320;
      const fallbackHeight = isMobile ? 200 : 180;
      const bubbleWidth = bubbleElement?.offsetWidth || fallbackWidth;
      const bubbleHeight = bubbleElement?.offsetHeight || fallbackHeight;

      const { top, left, arrow, arrowOffset } = computeBelowPlacement({
        rect,
        bubbleWidth,
        bubbleHeight,
        bottomNavbarHeight,
      });

      setBubblePosition({ top, left });
      setArrowPosition(arrow);
      setArrowOffset(arrowOffset);
      setIsPositioned(true);
    };

    let settleTimer;
    const scrollAndPosition = () => {
      const targetElement = document.getElementById(current.target);
      if (!targetElement) {
        if (process.env.NODE_ENV !== "production") {
          console.warn(`[PortfolioGuideSteps] no element #${current.target}, skipping step`);
        }
        skipToNextStepRef.current();
        return;
      }

      const rect = targetElement.getBoundingClientRect();
      const comfortablyVisible =
        rect.top > 80 && rect.bottom < window.innerHeight - bottomNavbarHeight - 120;
      if (!comfortablyVisible) {
        targetElement.scrollIntoView({
          behavior: "auto",
          block: "nearest",
          inline: "nearest",
        });
      }

      compute();
      settleTimer = setTimeout(compute, isMobile ? 150 : 220);
    };

    const timer = setTimeout(scrollAndPosition, 60);

    window.addEventListener("resize", compute, { passive: true });
    window.addEventListener("scroll", compute, { passive: true });
    return () => {
      clearTimeout(timer);
      clearTimeout(settleTimer);
      window.removeEventListener("resize", compute);
      window.removeEventListener("scroll", compute);
    };
  }, [current.target]);

  const handleNext = () => {
    if (stepIndex < steps.length - 1) {
      const nextStepIndex = stepIndex + 1;
      const nextStep = steps[nextStepIndex];

      // Auto-switch tab before showing next step (steps 2-4)
      if (nextStep.tabToShow && onTabChange) {
        onTabChange(nextStep.tabToShow);
        setTimeout(() => {
          setIsPositioned(false);
          setBubblePosition({ top: -1000, left: -1000 });
          setStepIndex(nextStepIndex);
        }, 350);
      } else {
        setIsPositioned(false);
        setBubblePosition({ top: -1000, left: -1000 });
        setStepIndex(nextStepIndex);
      }
      return;
    }
    setShowGuide(false);
  };

  const handleSkip = () => {
    const highlighted = document.querySelectorAll(".chart-guide-highlight");
    highlighted.forEach((el) => el.classList.remove("chart-guide-highlight"));
    setShowGuide(false);
  };

  useEffect(() => {
    return () => {
      const highlighted = document.querySelectorAll(".chart-guide-highlight");
      highlighted.forEach((el) => el.classList.remove("chart-guide-highlight"));
    };
  }, []);

  return (
    <>
      <div className="chart-guide-backdrop" onClick={handleSkip} />

      <div
        ref={bubbleRef}
        className={`chart-guide-bubble dynamic-position arrow-${arrowPosition} step-${stepIndex + 1} portfolio-guide-bubble`}
        style={{
          top: bubblePosition.top,
          left: bubblePosition.left,
          opacity: isPositioned ? 1 : 0,
          "--arrow-offset": arrowOffset,
          transition: "opacity 0.2s ease-in-out",
        }}
      >
        <div className="guide-step-indicator">
          Step {stepIndex + 1} of {steps.length}
        </div>
        <div className="guide-text">{current.text}</div>

        <div className="guide-buttons">
          <button className="next-btn" onClick={handleNext}>
            {stepIndex === steps.length - 1 ? "Finish" : "Next"}
          </button>
          <button className="skip-btn" onClick={handleSkip}>
            Skip
          </button>
        </div>
      </div>
    </>
  );
};

export default PortfolioGuideSteps;
